/* Ring builder — the gemstone step (step 2).
   Split out of ring-builder-pages.js because it is the most involved surface in
   the funnel and follows its own spec (scratchpad/STONE_BROWSER_SPEC.md, from a
   forensic teardown of Blue Nile and Brilliant Earth).

   THE THREE THINGS THAT MAKE IT READ AS PREMIUM, all learned from that teardown:
     1. The 4Cs are ORDERED-SCALE DUAL SLIDERS, not chips. Both benchmarks do this
        and neither uses a single checkbox among the 4Cs. Out-of-range grades are
        GRAYED, never hidden — seeing the whole ladder is what silently teaches a
        first-time buyer what VS1 means.
     2. TWO RAILS, and the color rail is SIMPLER, not richer. Brilliant Earth's
        diamond page has 19 filters; its gemstone page has 9 and no carat slider at
        all. Blue Nile's gemstone finder is four filters. Leading a color rail with
        the 4Cs would be a category error — a sapphire is not graded like a diamond.
     3. There is no "fits this setting" checkbox. Fit is a constraint on the control
        surface: incompatible shapes are DISABLED. Blue Nile has no such checkbox
        for exactly this reason.

   noUiSlider is already vendored by the Edge theme, so the sliders cost no new
   dependency — only the CSS override that removes its default shadowed puck. */
(function () {
  'use strict';
  var RB = window.RingBuilder;
  if (!RB) return;
  var el = RB.el, money = RB.money;

  var SHAPES = ['ROUND', 'OVAL', 'CUSHION', 'EMERALD', 'PEAR', 'RADIANT', 'SQUARE',
    'ASSCHER', 'MARQUISE', 'HEART', 'ELONGATED CUSHION', 'ANTIQUE CUSHION',
    'TRILLION', 'ELONGATED HEXAGON', 'OCTAGON', 'ELONGATED OVAL', 'STRAIGHT BAGUETTE'];

  // Ordered worst -> best, so a dual slider reads left-to-right as rising quality.
  //
  // 🚨 A GRADE MISSING FROM THESE LISTS IS NOT FILTERED OUT — IT IS FILTERED PAST.
  // `passes()` below reads `if (r._col >= 0 && …)`, and an unlisted grade gives
  // indexOf === -1, which SKIPS the guard: the stone then passes at EVERY slider
  // position and shows up inside ranges that exclude it. That is not a crash and
  // not an empty grid — it is a K-colour stone sitting in a "D–F only" result set,
  // looking exactly like a correct result. So these lists must cover every grade
  // the shards can hold. `verify_diamond_assets.py` parses this file and fails on
  // any grade the data carries and the ladder does not; run it after editing here.
  //
  // 🚨 K-D IS THE SCALE, AND AN OUTLIER DOES NOT GET TO REDRAW IT. `U-V` was briefly
  // added as a ninth rung because ONE stone of 7,991 carries it. That is the wrong
  // trade: it hands every shopper a rung they will never want, implies we stock the
  // L-through-T grades between it and K (we stock none), and stretches the eight
  // grades that matter into 8/9ths of the track. Grades off this ladder are handled
  // as OFF-SCALE instead — excluded from the slider, surfaced in their own chip row
  // — which is the same treatment fancy colour already gets and for the same reason:
  // they are not "worse K", they are not on this axis at all. See isOffScale below.
  var COLOR = ['K', 'J', 'I', 'H', 'G', 'F', 'E', 'D'];
  // SI3 sits between I1 and SI2; FL (Flawless) is the one grade above IF.
  var CLARITY = ['I1', 'SI3', 'SI2', 'SI1', 'VS2', 'VS1', 'VVS2', 'VVS1', 'IF', 'FL'];
  // 🚨 EXCELLENT IS ITS OWN RUNG, AND IT IS THE BIGGEST ONE. This ladder used to
  // read Fair/Good/Very Good/Ideal/Super Ideal and CUT_MAP folded `excellent` onto
  // the same index as `ideal`, on the theory that "labs use different words for the
  // same top grade". Measured over the 7,991 diamonds, that theory is wrong:
  //
  //     Excellent 3,200 (40.0%)   GIA 1,209 · IGI 1,076 · GCAL 911
  //     Very Good 2,567 (32.1%)
  //     Ideal     1,688 (21.1%)   IGI 955 · GCAL 702 · EGL 18 · AGS 13
  //     8X          508 ( 6.4%)   GCAL only
  //     Good         28 ( 0.4%)
  //
  // IGI and GCAL each issue BOTH words on different stones, so within a single lab
  // they are not synonyms — Ideal sits above Excellent. Folding them meant a shopper
  // asking for "Ideal and better" was handed Excellent stones too, and the single
  // most common grade in the catalogue had no rung bearing its name. GIA simply tops
  // out at Excellent (it does not issue "Ideal"), which is normal and now shows
  // truthfully rather than being relabelled.
  //
  // `Fair` stays despite ZERO diamonds carrying it: the BUILDER's shards hold 2, and
  // this ladder is shared. `Super Ideal` is the trade-readable label for GCAL's "8X"
  // (eight Excellent sub-grades) — the card still prints the lab's own word, because
  // showing "Super Ideal" beside a GCAL report number would misquote the report.
  var CUT = ['Fair', 'Good', 'Very Good', 'Excellent', 'Ideal', 'Super Ideal'];

  // FANCY COLOUR IS A DIFFERENT AXIS, NOT A WORSE GRADE. A Fancy Vivid Blue is not
  // "below K" — it is off the D-Z scale entirely, and putting it at either end of
  // that ladder would be a category error. So fancy stones are excluded from the
  // colour slider and get their own chips (see the Fancy color facet below).
  // `'Fa'` is Stuller's truncated code for the same thing; the metafield was
  // repaired to the real words by write_fancy_colour.py, but the BUILDER's shards
  // still carry the old code until they are next rebuilt, so both are matched.
  var isFancy = function (c) {
    c = String(c || '');
    return /^fancy/i.test(c) || c === 'Fa';
  };
  // "Fancy Vivid Orange Yellow" -> "Yellow". The hue is the last word; the words
  // before it are intensity, which belongs on the card, not in a chip row that
  // would otherwise carry twelve near-duplicate labels for five actual colours.
  var fancyHue = function (c) {
    var w = String(c || '').trim().split(/\s+/);
    return w.length ? w[w.length - 1] : '';
  };

  // OFF-SCALE: a real colour grade that is not on the K-D ladder and is not fancy —
  // today just "U-V" on a single stone, tomorrow whatever Stuller sends next. It
  // must never be silently dropped and must never silently PASS: `passes()` skips
  // its guard on an unknown grade, so without this an off-scale stone matched at
  // every slider position. Instead these are excluded from the slider and listed as
  // their own chips, so the eight grades a shopper actually shops by keep the whole
  // track. Derived rather than enumerated, so a new grade needs no code change to be
  // handled safely — only to be given a nicer label.
  var isOffScale = function (c) {
    c = String(c || '').trim();
    return !!c && !isFancy(c) && COLOR.indexOf(c) < 0;
  };

  // GIA grades the tinted end of the D-Z scale in PAIRS — L, M, N, then O-P, Q-R,
  // S-T, U-V, W-X, Y-Z — so a single stone reads "U-V", not "U". Bucketing them
  // under one **L–Z** chip is deliberate: pinning the row to whichever sub-range we
  // happen to hold today would rename itself every time stock turned over, and a
  // shopper looking at the tinted end is not shopping "U-V specifically", they are
  // shopping "warmer than K". One chip that stays put reads better and stays true.
  // The CARD still prints the stone's exact grade, so nothing is hidden.
  //
  // ⚠️ ONLY REAL D-Z DESIGNATIONS ARE BUCKETED. `isOffScale` is derived, so it also
  // catches junk that lands in a colour field (the way "Minute" — a fluorescence
  // value — landed in cut_grade on 2 stones). Labelling that "L–Z" would state
  // something false, so anything unrecognised keeps its own raw-valued chip.
  // ⚠️ ASCII HYPHEN, NOT AN EN-DASH. This value goes into the URL as `offcolor=`,
  // and an en-dash encodes to %E2%80%93 — a filter link nobody can read, type or
  // paste back reliably. Typography belongs in the label, not in the key.
  var BELOW_K = /^(?:[L-Z]|O-P|Q-R|S-T|U-V|W-X|Y-Z)$/i;
  var offKey = function (c) {
    c = String(c || '').trim();
    return BELOW_K.test(c) ? 'L-Z' : c;
  };

  // Every cut wording a lab can hand us, mapped to its rung in CUT above. This is
  // for FILTERING only — the card still prints the lab's own word, because showing
  // "Super Ideal" beside a GCAL report that says "8X" would misquote the report.
  //
  // ⚠️ A WORDING MISSING FROM HERE IS NOT FILTERED OUT, IT IS FILTERED PAST:
  // `passes()` reads `r._cut != null`, so an unmapped grade gives undefined, skips
  // the guard and matches at EVERY slider position. `verify_diamond_assets.py`
  // fails on any cut grade the shards carry that this map does not, which is the
  // only place that can be caught — the builder's own shards have no such check,
  // and they currently hold 2 stones whose cut_grade is "Minute", a FLUORESCENCE
  // value sitting in a cut field. It is deliberately NOT mapped: inventing a rung
  // for it would launder a data error into a grade.
  var CUT_MAP = {
    'fair': 0, 'good': 1, 'very good': 2,
    'excellent': 3, 'ex': 3,
    'ideal': 4,
    '8x': 5, 'super ideal': 5
  };

  var title = function (s) {
    return String(s || '').toLowerCase().replace(/(^|\s)\w/g, function (m) { return m.toUpperCase(); });
  };
  var caret = function () {
    var s = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    s.setAttribute('width', '10'); s.setAttribute('height', '6'); s.setAttribute('viewBox', '0 0 10 6');
    s.innerHTML = '<path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1"/>';
    return s;
  };

  function facet(name, open, body, hint) {
    var f = el('div', { class: 'rb-facet', 'data-open': open ? 'true' : 'false' });
    var btn = el('button', {
      class: 'rb-facet__title', type: 'button', 'aria-expanded': String(!!open),
      onclick: function () {
        var o = f.getAttribute('data-open') === 'true';
        f.setAttribute('data-open', o ? 'false' : 'true');
        btn.setAttribute('aria-expanded', String(!o));
      }
    }, [el('span', { text: name })]);
    btn.appendChild(caret());
    f.appendChild(btn);
    var b = el('div', { class: 'rb-facet__body' });
    if (hint) b.appendChild(el('p', { class: 'rb-facet__hint', text: hint }));
    (body || []).forEach(function (n) { if (n) b.appendChild(n); });
    f.appendChild(b);
    return f;
  }

  /* --------------------------------------------------- ordered-scale slider */
  // labels: the whole ladder, always visible. onChange gets [loIndex, hiIndex].
  function scaleSlider(labels, value, onChange) {
    var wrap = el('div', {});
    var s = el('div', { class: 'rb-slider' });
    wrap.appendChild(s);
    if (!window.noUiSlider) {
      // graceful: without the library the rail still renders, just without this control
      wrap.appendChild(el('p', { class: 'rb-facet__hint', text: labels.join(' · ') }));
      return wrap;
    }
    window.noUiSlider.create(s, {
      start: [value[0], value[1]],
      connect: true,
      step: 1,
      range: { min: 0, max: labels.length },
      pips: { mode: 'values', values: labels.map(function (_, i) { return i + 0.5; }),
              density: 100, format: { to: function (v) { return labels[Math.floor(v)] || ''; } } }
    });
    function paint(lo, hi) {
      s.querySelectorAll('.noUi-value').forEach(function (n, i) {
        n.classList.toggle('is-in', i >= lo && i < hi);
      });
    }
    s.noUiSlider.on('update', function (vals) {
      paint(Math.round(vals[0]), Math.round(vals[1]));
    });
    s.noUiSlider.on('change', function (vals) {
      onChange([Math.round(vals[0]), Math.round(vals[1])]);
    });
    return wrap;
  }

  /* -------------------------------------------------------- numeric slider */
  function rangeSlider(opts, value, onChange, fmt) {
    var wrap = el('div', {});
    var s = el('div', { class: 'rb-slider' });
    wrap.appendChild(s);
    var lo = el('input', { type: 'text', inputmode: 'decimal', 'aria-label': opts.loLabel });
    var hi = el('input', { type: 'text', inputmode: 'decimal', 'aria-label': opts.hiLabel });
    if (!window.noUiSlider) return wrap;
    window.noUiSlider.create(s, {
      start: [value[0], value[1]], connect: true, range: opts.range, step: opts.step || null
    });
    s.noUiSlider.on('update', function (v) {
      lo.value = fmt(+v[0]); hi.value = fmt(+v[1]);
    });
    s.noUiSlider.on('change', function (v) { onChange([+v[0], +v[1]]); });
    function commit(which) {
      return function () {
        var n = parseFloat(String(this.value).replace(/[^0-9.]/g, ''));
        if (isNaN(n)) { s.noUiSlider.get(); return; }
        var cur = s.noUiSlider.get().map(Number);
        cur[which] = n;
        s.noUiSlider.set(cur);
        onChange(s.noUiSlider.get().map(Number));
      };
    }
    lo.addEventListener('change', commit(0));
    hi.addEventListener('change', commit(1));
    wrap.appendChild(el('div', { class: 'rb-range-io' }, [lo, el('span', { text: '–' }), hi]));
    wrap.appendChild(el('div', { class: 'rb-range-lbl' }, [
      el('span', { text: opts.loLabel }), el('span', { text: opts.hiLabel })
    ]));
    return wrap;
  }

  /* ------------------------------------------------------------- the page */
  function mount(root, build, origin) {
    var q = new URLSearchParams(location.search);
    var setting = build.style ? RB.setting(build.style) : null;
    var rail = root.querySelector('[data-rb-rail]');
    var grid = root.querySelector('[data-rb-grid]');
    var countEl = root.querySelector('[data-rb-count]');
    var toolbar = root.querySelector('[data-rb-toolbar]');

    var shape = q.get('shape') || build.shape ||
      (setting && setting.shapes.indexOf('ROUND') >= 0 ? 'ROUND' : setting && setting.shapes[0]) || 'ROUND';
    // A PEARL seat can never hold a diamond, so defaulting a pearl build to the
    // Diamonds tab greeted the shopper with "0 natural diamonds that fit your
    // setting" and an empty grid — the setting looked broken when it was simply
    // the wrong tab. An explicit ?type= still wins, so the toggle keeps working.
    var kind = q.get('type') ? (q.get('type') === 'color' ? 'color' : 'diamond')
                             : (shape === 'PEARL' ? 'color' : 'diamond');

    function apply(patch) {
      var n = new URLSearchParams(location.search);
      Object.keys(patch).forEach(function (k) {
        if (patch[k] == null) n.delete(k); else n.set(k, patch[k]);
      });
      location.search = n.toString();
    }
    var num = function (k, d) { var v = parseFloat(q.get(k)); return isNaN(v) ? d : v; };

    grid.innerHTML = '';
    for (var i = 0; i < 6; i++) grid.appendChild(el('div', { class: 'rb-skel' }));

    RB.loadShard(shape, origin === 'lab' ? 'lab' : 'nat').then(function (shard) {
      var all = RB.rows(shard).filter(function (r) { return r.available; });
      all.forEach(function (r) {
        r._cut = CUT_MAP[String(r.cut || '').toLowerCase()];
        r._col = COLOR.indexOf(r.color);
        r._cla = CLARITY.indexOf(r.clarity);
        r._isD = r.species === 'Diamond';
        r._fancy = isFancy(r.color);
        r._hue = r._fancy ? fancyHue(r.color) : null;
        // The shard groups pearls under shape PEARL (build_builder_assets.stone_shape
        // maps gem_type "Pearl" there, because a pearl's own stone_shape is "Round"
        // while the mounting calls its seat PEARL). `species` is the title's last
        // word, so a pearl reads "Pearl" — but the SHARD is the reliable signal.
        r._isPearl = shape === 'PEARL' || r.species === 'Pearl';
        r._lw = (r.l && r.w) ? r.l / r.w : null;
      });
      var pool = all.filter(function (r) { return kind === 'diamond' ? r._isD : !r._isD; });

      // FIT IS A REAL FILTER, NOT A CAPTION. The count used to append the words
      // "that fit your setting" while nothing was filtered — so it stated
      // something false, and a stone the mounting cannot hold was one click from
      // the cart. A stone qualifies if ANY seat of the chosen style can take it
      // (the seat then snaps to that stone; see RB.bestSeat), which is the only
      // reading of "fits your setting" that is true.
      if (setting) {
        pool = pool.filter(function (r) { return !!RB.bestSeat(setting.style, r); });
      }

      // bounds come from the pool, so a slider never offers an empty span
      var prices = pool.map(function (r) { return r.price; });
      var carats = pool.map(function (r) { return r.carat; }).filter(Boolean);
      var pMin = prices.length ? Math.floor(Math.min.apply(null, prices)) : 0;
      var pMax = prices.length ? Math.ceil(Math.max.apply(null, prices)) : 1000;
      var cMin = carats.length ? Math.min.apply(null, carats) : 0.2;
      var cMax = carats.length ? Math.max.apply(null, carats) : 12.6;

      var st = {
        price: [num('pmin', pMin), num('pmax', pMax)],
        carat: [num('ctmin', cMin), num('ctmax', cMax)],
        col: [parseInt(q.get('colmin') || 0, 10), parseInt(q.get('colmax') || COLOR.length, 10)],
        cla: [parseInt(q.get('clmin') || 0, 10), parseInt(q.get('clmax') || CLARITY.length, 10)],
        cut: [parseInt(q.get('cutmin') || 0, 10), parseInt(q.get('cutmax') || CUT.length, 10)],
        species: (q.get('sp') || '').split(',').filter(Boolean),
        fancy: (q.get('fancy') || '').split(',').filter(Boolean),
        sort: q.get('sort') || 'best'
      };
      // "Has the shopper expressed a colour preference at all?" — the slider sitting
      // at its full span means no. It decides whether fancy stones belong in an
      // unfiltered list (they do) or in a narrowed one (they do not): asking for
      // D–F is asking for COLOURLESS, and answering it with a Fancy Vivid Blue
      // would be answering a different question.
      var colWideOpen = st.col[0] === 0 && st.col[1] >= COLOR.length;

      /* ---------------- rail ---------------- */
      rail.innerHTML = '';

      // Only shapes THIS setting can hold are enabled. Disabled, never hidden.
      var shapeList = SHAPES.filter(function (x) {
        return Object.keys((window.RB_STONES_META || { shards: {} }).shards)
          .some(function (k) { return k.split('|')[0] === x; });
      });
      var allowed = setting ? setting.shapes : shapeList;
      // HOW MANY STONES ARE IN EACH SHAPE, for the origin and kind on screen.
      // Without this the shape carried over from step 1 silently hides the
      // catalogue: natural colour is 88 oval and 67 emerald but exactly ONE
      // round, so a shopper who picked a round setting sees a single stone and
      // reasonably concludes we do not stock coloured gems.
      var KINDS = (window.RB_STONES_META || {}).kinds || {};
      var nFor = function (x) {
        var k = KINDS[x + '|' + (origin === 'lab' ? 'lab' : 'nat')];
        return k ? (kind === 'diamond' ? k.d : k.c) : 0;
      };
      rail.appendChild(facet('Shape', true, [
        el('div', { class: 'rb-chipgrid' }, shapeList.map(function (x) {
          var fits = allowed.indexOf(x) >= 0;
          var n = nFor(x);
          var ok = fits && n > 0;
          return el('button', {
            class: 'rb-chip', type: 'button', 'aria-pressed': String(x === shape),
            disabled: ok ? null : 'disabled',
            title: !fits ? 'Your setting cannot take this shape'
                         : (n ? null : 'No ' + (kind === 'diamond' ? 'diamonds' : 'colored stones') +
                                       ' in this shape'),
            onclick: function () { if (ok) apply({ shape: x }); }
          }, [
            el('span', { class: 'rb-chip__cap', text: title(x) }),
            el('span', { class: 'rb-chip__n', text: n ? String(n) : '—' })
          ]);
        }))
      ], setting ? 'Counts are stones we stock in each shape. Grayed shapes will not ' +
                   'fit the setting you chose, or hold none of these stones.'
                 : 'Counts are stones we stock in each shape.'));

      if (kind === 'diamond') {
        rail.appendChild(facet('What are you looking for?', true, [
          el('div', { class: 'rb-presets' }, [
            ['sparkle', 'Most sparkle', 'The best-cut stones — cut drives brilliance more than any other grade.'],
            ['balance', 'Best balance', 'Near-colourless and eye-clean, where most of the money goes into size.'],
            ['size', 'Biggest look', 'Maximum carat for the budget, easing color and clarity to get there.']
          ].map(function (p) {
            return el('button', {
              class: 'rb-preset', type: 'button', 'aria-pressed': String(q.get('preset') === p[0]),
              text: p[1],
              onclick: function () {
                // 🚨 GRADES BY NAME, NEVER BY POSITION. These were literal indices
                // (colmin: 4, clmin: 3), which silently changed meaning the moment
                // a ladder gained an entry: adding U-V and SI3/FL shifted every one
                // of them by a place, so "Best balance" quietly became H-and-better
                // instead of G. Nothing failed and nothing looked wrong — the
                // preset just stopped meaning what its own description says.
                // indexOf keeps the intent readable and immune to the next entry.
                if (q.get('preset') === p[0]) return apply({ preset: null, cutmin: null, colmin: null, clmin: null });
                if (p[0] === 'sparkle') apply({ preset: p[0], cutmin: CUT.indexOf('Ideal'), colmin: null, clmin: null });
                if (p[0] === 'balance') apply({ preset: p[0], cutmin: CUT.indexOf('Very Good'), colmin: COLOR.indexOf('G'), clmin: CLARITY.indexOf('VS2') });
                if (p[0] === 'size') apply({ preset: p[0], cutmin: CUT.indexOf('Good'), colmin: COLOR.indexOf('J'), clmin: CLARITY.indexOf('SI2') });
              }
            });
          })),
          q.get('preset') ? el('p', { class: 'rb-preset__why',
            text: ({ sparkle: 'The best-cut stones — cut drives brilliance more than any other grade.',
                     balance: 'Near-colourless and eye-clean, where most of the money goes into size.',
                     size: 'Maximum carat for the budget, easing color and clarity to get there.' })[q.get('preset')] }) : null
        ]));
      }

      rail.appendChild(facet('Price', true, [
        rangeSlider({ range: { min: pMin, max: pMax }, step: 5,
                      loLabel: 'Min price', hiLabel: 'Max price' },
          st.price, function (v) { apply({ pmin: Math.round(v[0]), pmax: Math.round(v[1]) }); },
          function (n) { return money(n); })
      ]));

      if (kind === 'diamond') {
        rail.appendChild(facet('Carat', true, [
          rangeSlider({ range: { min: cMin, max: cMax }, step: 0.01,
                        loLabel: 'Min carat', hiLabel: 'Max carat' },
            st.carat, function (v) { apply({ ctmin: v[0].toFixed(2), ctmax: v[1].toFixed(2) }); },
            function (n) { return n.toFixed(2); })
        ]));
        rail.appendChild(facet('Color', true, [
          scaleSlider(COLOR, st.col, function (v) { apply({ colmin: v[0], colmax: v[1], preset: null }); })
        ]));

        // FANCY COLOUR — its own facet, and only where there is fancy stock to
        // offer. Counted off the pool rather than hard-coded, so a shape with no
        // fancy stones shows no empty row and the counts are always true.
        // Chips are HUES (five) rather than grades (twelve): "Fancy Vivid Pink"
        // and "Fancy Light Pink" are the same request, and the intensity is on the
        // card where it belongs.
        var hues = {};
        pool.forEach(function (r) {
          if (r._fancy && r._hue) hues[r._hue] = (hues[r._hue] || 0) + 1;
        });
        var hueKeys = Object.keys(hues).sort(function (a, b) { return hues[b] - hues[a]; });
        if (hueKeys.length) {
          rail.appendChild(facet('Fancy color', st.fancy.length > 0, [
            el('div', { class: 'rb-chipgrid' }, hueKeys.map(function (h) {
              var on = st.fancy.indexOf(h) >= 0;
              return el('button', {
                class: 'rb-chip', type: 'button', 'aria-pressed': String(on),
                onclick: function () {
                  var next = on ? st.fancy.filter(function (x) { return x !== h; })
                                : st.fancy.concat([h]);
                  // Picking a hue clears the colourless slider, because the two
                  // ask opposite questions and leaving a stale D–F in the URL
                  // would read as a filter that is on and doing nothing.
                  apply({ fancy: next.join(',') || null, colmin: null, colmax: null,
                          preset: null });
                }
              }, [
                el('span', { class: 'rb-chip__cap', text: h }),
                el('span', { class: 'rb-chip__n', text: String(hues[h]) })
              ]);
            }))
          ], 'Naturally coloured diamonds, graded on hue and intensity instead of ' +
             'the D–Z scale. Choosing one sets the colour slider aside.'));
        }

        rail.appendChild(facet('Clarity', true, [
          scaleSlider(CLARITY, st.cla, function (v) { apply({ clmin: v[0], clmax: v[1], preset: null }); })
        ]));
        rail.appendChild(facet('Cut', true, [
          scaleSlider(CUT, st.cut, function (v) { apply({ cutmin: v[0], cutmax: v[1], preset: null }); })
        ], 'Labs word the top grade differently — we group Excellent and Ideal together here, and print each report’s own wording on the stone.'));
      } else {
        // COLOR RAIL — species leads. Both benchmarks lead on what the stone IS.
        var species = {};
        pool.forEach(function (r) { if (r.species) species[r.species] = (species[r.species] || 0) + 1; });
        var top = Object.keys(species).sort(function (a, b) { return species[b] - species[a]; });
        rail.appendChild(facet('Stone', true, [
          el('div', {}, top.map(function (sp) {
            return el('label', { class: 'rb-check' }, [
              el('input', { type: 'checkbox', checked: st.species.indexOf(sp) >= 0 ? 'checked' : null,
                onchange: function () {
                  var next = st.species.indexOf(sp) >= 0
                    ? st.species.filter(function (x) { return x !== sp; })
                    : st.species.concat([sp]);
                  apply({ sp: next.join(',') || null });
                } }),
              el('span', { text: sp }),
              el('span', { class: 'rb-check__count', text: String(species[sp]) })
            ]);
          }))
        ]));
        rail.appendChild(facet('Everyday wear', false, [
          el('p', { class: 'rb-facet__hint',
            text: 'Softer stones are still beautiful rings — they just want a gentler life. Nothing is hidden here.' })
        ]));
      }

      /* ---------------- toolbar ---------------- */
      toolbar.innerHTML = '';
      var segs = el('div', { class: 'rb-seg' }, ['diamond', 'color'].map(function (k) {
        return el('button', {
          type: 'button', 'aria-pressed': String(k === kind),
          text: k === 'diamond' ? 'Diamonds' : 'Colored stones',
          onclick: function () { apply({ type: k, preset: null }); }
        });
      }));
      toolbar.appendChild(segs);

      /* ---------------- filter + sort ---------------- */
      function passes(r) {
        if (r.price < st.price[0] || r.price > st.price[1]) return false;
        if (kind === 'diamond') {
          if (r.carat != null && (r.carat < st.carat[0] || r.carat > st.carat[1])) return false;
          // FANCY IS JUDGED BY ITS OWN FACET AND NEVER BY THE D-Z SLIDER. Before
          // this, `_col` was -1 on every fancy stone and the colour guard was
          // SKIPPED, so all 294 passed at every slider position — a Fancy Vivid
          // Blue answered a request for "D–F". Clarity and cut still apply: those
          // are graded the same way on a fancy stone.
          if (r._fancy) {
            if (st.fancy.length) {
              if (st.fancy.indexOf(r._hue) < 0) return false;
            } else if (!colWideOpen) {
              return false;
            }
          } else if (isOffScale(r.color)) {
            // OFF-SCALE (U-V and anything else not on K-D) gets the fancy treatment
            // rather than a rung of its own: shown while no colour preference is
            // expressed, dropped the moment one is. Without this branch `_col` is
            // -1, the guard below is SKIPPED and the stone matches at every position.
            if (st.fancy.length || !colWideOpen) return false;
          } else {
            // Asking for a fancy hue is asking for fancy stones only.
            if (st.fancy.length) return false;
            if (r._col >= 0 && (r._col < st.col[0] || r._col >= st.col[1])) return false;
          }
          if (r._cla >= 0 && (r._cla < st.cla[0] || r._cla >= st.cla[1])) return false;
          if (r._cut != null && (r._cut < st.cut[0] || r._cut >= st.cut[1])) return false;
        } else if (st.species.length && st.species.indexOf(r.species) < 0) return false;
        return true;
      }
      var rows = pool.filter(passes);

      var seatL = build.seat ? RB.parseSeat(build.seat).l : null;
      var SORTS = {
        // "Best match" is something neither benchmark can compute, because we know
        // the seat: the stone that fills the chosen center most exactly, best cut first.
        best: function (a, b) {
          if (seatL) {
            var d = Math.abs((a.l || 99) - seatL) - Math.abs((b.l || 99) - seatL);
            if (Math.abs(d) > 0.005) return d;
          }
          var c = (b._cut || 0) - (a._cut || 0);
          return c || a.price - b.price;
        },
        'price-asc': function (a, b) { return a.price - b.price; },
        'price-desc': function (a, b) { return b.price - a.price; },
        'carat-asc': function (a, b) { return (a.carat || 0) - (b.carat || 0); },
        'carat-desc': function (a, b) { return (b.carat || 0) - (a.carat || 0); }
      };
      rows.sort(SORTS[st.sort] || SORTS.best);

      /* ---- active-filter chips + reset. Both benchmarks show what is ON and
         let you remove it individually; zero vertical cost when nothing is set. */
      var activeWrap = root.querySelector('[data-rb-active]');
      if (activeWrap) {
        activeWrap.innerHTML = '';
        var chips = [];
        function chip(label, patch) { chips.push({ label: label, patch: patch }); }
        if (st.price[0] > pMin || st.price[1] < pMax)
          chip(money(st.price[0]) + ' – ' + money(st.price[1]), { pmin: null, pmax: null });
        if (kind === 'diamond') {
          if (st.carat[0] > cMin || st.carat[1] < cMax)
            chip(st.carat[0].toFixed(2) + ' – ' + st.carat[1].toFixed(2) + ' ct',
                 { ctmin: null, ctmax: null });
          if (st.col[0] > 0 || st.col[1] < COLOR.length)
            chip('Color ' + COLOR[st.col[1] - 1] + '–' + COLOR[st.col[0]], { colmin: null, colmax: null });
          if (st.cla[0] > 0 || st.cla[1] < CLARITY.length)
            chip('Clarity ' + CLARITY[st.cla[1] - 1] + '–' + CLARITY[st.cla[0]], { clmin: null, clmax: null });
          if (st.cut[0] > 0 || st.cut[1] < CUT.length)
            chip('Cut ' + CUT[st.cut[0]] + ' and better', { cutmin: null, cutmax: null });
        }
        st.species.forEach(function (sp) {
          chip(sp, { sp: st.species.filter(function (x) { return x !== sp; }).join(',') || null });
        });
        st.fancy.forEach(function (h) {
          chip('Fancy ' + h,
               { fancy: st.fancy.filter(function (x) { return x !== h; }).join(',') || null });
        });
        chips.forEach(function (c) {
          activeWrap.appendChild(el('button', {
            class: 'rb-active__chip', type: 'button',
            'aria-label': 'Remove filter ' + c.label,
            onclick: function () { apply(Object.assign({ preset: null }, c.patch)); }
          }, [el('span', { text: c.label }), el('span', { text: '✕', 'aria-hidden': 'true' })]));
        });
        if (chips.length) {
          activeWrap.appendChild(el('button', {
            class: 'rb-active__reset', type: 'button', text: 'Reset all',
            onclick: function () {
              apply({ pmin: null, pmax: null, ctmin: null, ctmax: null, colmin: null,
                      colmax: null, clmin: null, clmax: null, cutmin: null, cutmax: null,
                      sp: null, fancy: null, preset: null });
            }
          }));
        }
        root.__activeCount = chips.length;
      }

      countEl.innerHTML = '';
      countEl.appendChild(el('span', {
        html: '<strong>' + rows.length.toLocaleString() + '</strong> ' +
              (origin === 'lab' ? 'lab-grown ' : 'natural ') +
              (kind === 'diamond' ? (rows.length === 1 ? 'diamond' : 'diamonds')
                                  : (rows.length === 1 ? 'colored stone' : 'colored stones')) +
              (setting ? ' that fit your setting' : '')
      }));

      /* ---- sort: current value IN the trigger, options grouped by axis ---- */
      var SORT_LABEL = { best: 'Best match', 'price-asc': 'Price: low to high',
        'price-desc': 'Price: high to low', 'carat-asc': 'Carat: low to high',
        'carat-desc': 'Carat: high to low' };
      var sortWrap = root.querySelector('[data-rb-sort]');
      if (sortWrap) {
        sortWrap.innerHTML = '';
        var menu = el('div', { class: 'rb-sort__menu', role: 'listbox', hidden: 'hidden' });
        var btn = el('button', {
          class: 'rb-sort__btn', type: 'button', 'aria-haspopup': 'listbox',
          'aria-expanded': 'false',
          onclick: function () {
            var open = !menu.hidden;
            menu.hidden = open;
            btn.setAttribute('aria-expanded', String(!open));
          }
        }, [el('span', { text: 'Sort by: ' + (SORT_LABEL[st.sort] || 'Best match') })]);
        btn.appendChild(caret());
        [['', ['best']], ['Price', ['price-asc', 'price-desc']],
         kind === 'diamond' ? ['Carat', ['carat-asc', 'carat-desc']] : null
        ].filter(Boolean).forEach(function (grp) {
          if (grp[0]) menu.appendChild(el('div', { class: 'rb-sort__group', text: grp[0] }));
          grp[1].forEach(function (k) {
            menu.appendChild(el('button', {
              class: 'rb-sort__opt', type: 'button', role: 'option',
              'aria-selected': String(k === st.sort),
              text: grp[0] ? SORT_LABEL[k].split(': ')[1] : SORT_LABEL[k],
              onclick: function () { apply({ sort: k }); }
            }));
          });
        });
        document.addEventListener('click', function (e) {
          if (!sortWrap.contains(e.target)) { menu.hidden = true; btn.setAttribute('aria-expanded', 'false'); }
        });
        sortWrap.appendChild(btn);
        sortWrap.appendChild(menu);
      }

      /* ---- mobile: rail becomes a drawer ---- */
      var fb = root.querySelector('[data-rb-filterbar]');
      if (fb) {
        fb.innerHTML = '';
        var n = root.__activeCount || 0;
        var openBtn = el('button', { type: 'button', 'aria-expanded': 'false' },
          [el('span', { text: 'Filters' })]);
        if (n) openBtn.appendChild(el('span', { class: 'rb-fb-count', text: String(n) }));
        var scrim = root.querySelector('[data-rb-scrim]');
        function setOpen(open) {
          rail.setAttribute('data-open', String(open));
          if (scrim) scrim.hidden = !open;
          openBtn.setAttribute('aria-expanded', String(open));
          document.body.style.overflow = open ? 'hidden' : '';
          if (open && window.trapFocus) window.trapFocus(rail, rail.querySelector('button'));
          else if (window.removeTrapFocus) window.removeTrapFocus(openBtn);
        }
        openBtn.addEventListener('click', function () { setOpen(true); });
        if (scrim) scrim.addEventListener('click', function () { setOpen(false); });
        document.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' && rail.getAttribute('data-open') === 'true') setOpen(false);
        });
        fb.appendChild(openBtn);
        var sortBtn = el('button', { type: 'button', text: SORT_LABEL[st.sort] || 'Sort',
          onclick: function () { var b = root.querySelector('.rb-sort__btn'); if (b) b.click(); } });
        fb.appendChild(sortBtn);
        // drawer chrome, prepended once
        if (!rail.querySelector('.rb-rail__head')) {
          rail.insertBefore(el('div', { class: 'rb-rail__head' }, [
            el('strong', { text: 'Filters' }),
            el('button', { class: 'rb-rail__close', type: 'button', 'aria-label': 'Close filters',
              text: '✕', onclick: function () { setOpen(false); } })
          ]), rail.firstChild);
          rail.appendChild(el('div', { class: 'rb-rail__apply' }, [
            el('button', { class: 'btn btn-primary', type: 'button',
              text: 'Show ' + rows.length.toLocaleString() + ' stones',
              onclick: function () { setOpen(false); } })
          ]));
        }
      }

      /* ---------------- results ---------------- */
      grid.innerHTML = '';
      if (!rows.length) {
        grid.appendChild(el('div', { class: 'rb-empty' }, [
          el('p', { text: 'No stones match those filters.' }),
          el('p', { text: 'Try widening price or clarity — or switch to ' +
            (kind === 'diamond' ? 'colored stones' : 'diamonds') + '.' }),
          // 🚨 REBUILD FROM `build`, NOT FROM A HAND-LISTED FIVE. This listed
          // style/shape/seat/metal/accent, so "Reset all filters" also reset the
          // shopper's CHOSEN STONE and their origin — the same partial-copy bug
          // that made the setting step drop the stone (see ring-builder-pages.js,
          // `chosen`). Resetting FILTERS must never reset the BUILD. It also wrote
          // empty strings for absent fields, which RB.read() drops but which
          // litter the address bar; RB.href omits them properly.
          el('button', { class: 'rb-active__reset', type: 'button', text: 'Reset all filters',
            onclick: function () {
              location.href = RB.href(location.pathname, build, { shape: shape });
            } })
        ]));
        return;
      }

      var PAGE = 60, shown = 0;
      function draw() {
        rows.slice(shown, shown + PAGE).forEach(function (r) {
          // A PEARL is graded on none of carat / colour / clarity / cut — it is sold
          // by millimetre, body colour and a letter quality (AA, AAA). Running it
          // through either branch below printed a bare "7.00 mm · Pearl" and dropped
          // everything a pearl buyer actually chooses on. Owner decision 2026-07-29:
          // size · type · quality. Type and quality are read off the title, which is
          // where they already live ("7 mm White Cultured Akoya Pearl – Round Shape
          // – Half-Drilled – AA Quality").
          var spec;
          if (r._isPearl) {
            var t = r.title || '';   // RB.rows exposes `title`; `t` is the shard COLUMN name
            var mm = r.l ? (r.l % 1 ? r.l.toFixed(1) : r.l.toFixed(0)) + ' mm' : null;
            var body = (t.match(/\b(White|Black|Pink|Peach|Lavender|Golden|Grey|Gray)\b/) || [])[1];
            var kind = (t.match(/\b(Akoya|Freshwater|South Sea|Tahitian)\b/) || [])[1];
            var qual = (t.match(/–\s*(AAA|AA\+?|A)\s*Quality/i) || [])[1];
            spec = [mm, [body, kind].filter(Boolean).join(' ') || 'Cultured Pearl', qual]
              .filter(Boolean).join(' · ');
          } else if (r._isD) {
            spec = [r.carat ? r.carat.toFixed(2) + ' ct' : null, r.color, r.clarity, r.cut]
              .filter(Boolean).join(' · ');
          } else {
            spec = [r.carat ? r.carat.toFixed(2) + ' ct' : (r.l ? r.l.toFixed(2) + ' mm' : null), r.species]
              .filter(Boolean).join(' · ');
          }
          grid.appendChild(el('a', {
            class: 'rb-card',
            // carry the seat that actually holds THIS stone, not the one chosen
            // back in step 1b before the customer knew what they wanted
            href: RB.href(RB.pages.stone, build, {
              stone: r.vid, origin: origin, shape: shape,
              seat: (setting && RB.bestSeat(setting.style, r)) || build.seat || null
            })
          }, [
            el('span', { class: 'rb-card__media' }, [
              r.img ? el('img', { src: r.img, alt: r.title, loading: 'lazy' })
                    : el('span', { class: 'rb-noimg', text: 'Photograph to follow' }),
              r.wear && r.wear.indexOf('Excellent') < 0
                ? el('span', { class: 'rb-card__flag', text: r.wear }) : null
            ]),
            el('span', { class: 'rb-card__body' }, [
              el('span', { class: 'rb-card__title', text: r.title }),
              el('span', { class: 'rb-card__spec', text: spec }),
              el('span', { class: 'rb-card__price', text: money(r.price) })
            ])
          ]));
        });
        shown += PAGE;
        var old = grid.parentNode.querySelector('.rb-more');
        if (old) old.remove();
        if (shown < rows.length) {
          grid.parentNode.appendChild(el('button', {
            class: 'btn btn-outline rb-more', type: 'button',
            text: 'View more (' + (rows.length - shown).toLocaleString() + ' more)',
            onclick: draw
          }));
        }
      }
      draw();
    });
  }

  RB.mountStones = mount;

  // 🚨 THE GRADE SCALES ARE EXPORTED, AND THIS FILE IS THEIR ONLY DEFINITION.
  // `diamond-browse.js` (the collection-page browser) filters on exactly the same
  // ladders, and a second copy of them is the `chain_fit` bug waiting to happen —
  // two implementations of one rule that agree until the day someone edits one.
  // `verify_diamond_assets.py` parses THIS file and fails on any grade the shards
  // carry that the ladder does not, so keeping one definition here keeps that
  // check meaningful for both surfaces.
  RB.SCALES = {
    COLOR: COLOR, CLARITY: CLARITY, CUT: CUT, CUT_MAP: CUT_MAP,
    isFancy: isFancy, fancyHue: fancyHue, isOffScale: isOffScale, offKey: offKey
  };
})();
